import React, { useEffect, useState } from 'react';
import { InputElementWrapper } from './OdfjellInputComponent.style';
import { ValidationItem } from '../../../types/ViewModel/ValidationItemType';
import { roundTo2Decimals } from '../../../utils/numberHelper';
import { Duration } from 'luxon';
import {
    InputValidatorType,
    validateCustomValidations,
    validateInputFromEvent,
    validateInputFromProps,
    validateTimeInput,
} from './InputValidatorUtils';
import NumberInputComponent from './number-input/NumberInput';
import TextInputComponent from './text-input/TextInput';
import TextAreaInputComponent from './text-area-input/TextAreaInput';
import SelectInputComponent from './select-input/SelectInput';
import CheckboxInputComponent from './checkbox-input/CheckboxInput';
import TimeInputComponent from './time-input/TimeInput';
import DateInputComponent from './date-input/DateInput';
import ValidationComponent from './validation-component/ValidationCompnent';
import CostComponent from './cost-component/CostComponent';
import TooltipComponent from './tooltip-component/TooltipComponent';
import ReadonlyComponent from './readonly-component/ReadonlyComponent';
import Tooltip from 'src/components/ui/tooltip/Tooltip';
import { WarningText } from 'src/components/shared/warning-text/WarningText.style';

export type InputType = 'number' | 'select' | 'date' | 'checkbox' | 'text' | 'time' | 'textarea' | 'duration';
export type InputSizeValues = 'small' | 'medium' | 'large';
export type AlignValues = 'left' | 'right';
export type InputChangeData = { event: any; isValid?: boolean; dataObject?: any; extraArgs?: any; inputId?: string | number };
export type TimeInputChangeData = {
    hours: number;
    isValid?: boolean;
    dataObject?: any;
    name?: string;
    durationString?: string;
    inputId?: string | number;
};
export type DateInputChangeData = { date: Date; isValid?: boolean; dataObject?: any; name?: string; inputId?: string | number };

export type InputProps = {
    type: InputType;
    isReadOnly?: boolean;
    name?: string;
    description?: string;
    title?: string;
    value?: any;
    options?: any[];
    unit?: string;
    min?: number;
    max?: number;
    cost?: number;
    dataObject?: any;
    extraArgs?: any;
    customValidations?: ValidationItem[];
    onChange?: (changeObject: InputChangeData) => void;
    onTimeInputChange?: (changeObject: TimeInputChangeData) => void;
    onDateInputChange?: (changeObject: DateInputChangeData) => void;
    hideIfEmpty?: boolean;
    leaveBlank?: boolean;
    allowNegativeValues?: boolean;
    tooltipId?: string;
    disableUnit?: boolean;
    selectDefaultOption?: string;
    tabIndex?: number;
    validationInTooltip?: boolean;
    warningText?: string;
    inputId?: string | number;
    elementId?: string;
    maxDecimals?: number | null;
    maxTextLength?: number | null;
    inputSize?: InputSizeValues;
    textAlign?: AlignValues;
    labelMinWidth?: number;
};

const DEFAULT_DECIMALS = 2;

const OdfjellInputComponent: React.FC<InputProps> = (props: InputProps) => {
    const { leaveBlank = true } = props;
    const [isValid, setIsValid] = useState(true);
    const [validation, setValidation] = useState<InputValidatorType | null>(null);

    const [selectedCustomValidation, setSelectedCustomValidation] = useState<ValidationItem>();

    useEffect(() => {
        const _validation = validateInputFromProps(props.min, props.max, props.value, props.customValidations);
        setValidation({ ..._validation });
    }, [props]);

    useEffect(() => {
        if (!validation) return;
        updateValidation(validation);
    }, [validation]);

    const updateValidation = (validation: InputValidatorType) => {
        setIsValid(validation.isValid);
        if (validation.customValidation) {
            setSelectedCustomValidation(validation.customValidation);
        } else if (selectedCustomValidation) {
            setSelectedCustomValidation(undefined);
        }
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        const duration = Duration.fromISOTime(changeObject.durationString);
        const hoursDurationRaw = duration.hours + duration.minutes / 60;
        const hoursDuration = roundTo2Decimals(hoursDurationRaw);

        if (props.onTimeInputChange && props.dataObject && props.name) {
            const _validation = validateTimeInput(hoursDuration, props.min, props.max);
            setValidation({ ..._validation });
            props.onTimeInputChange({
                hours: hoursDuration,
                isValid: _validation.isValid,
                dataObject: props.dataObject,
                name: props.name,
                inputId: props.inputId,
            });
        }
    };

    const onDateInputChange = (changeObject: DateInputChangeData) => {
        if (props.onDateInputChange && props.name && changeObject.date) {
            const _validation = validateCustomValidations(props.customValidations, changeObject.date);
            setValidation({ ..._validation });
            props.onDateInputChange({
                date: changeObject.date,
                isValid: _validation.isValid,
                dataObject: props.dataObject,
                name: props.name,
                inputId: props.inputId,
            });
        }
    };

    const handleChange = (changeObject: InputChangeData) => {
        if (props.onChange) {
            const _validation = validateInputFromEvent(changeObject.event, props.customValidations);
            setValidation({ ..._validation });
            props.onChange({
                event: changeObject.event,
                isValid: _validation.isValid,
                dataObject: props.dataObject,
                extraArgs: props.extraArgs,
                inputId: props.inputId,
            });
        }
    };

    const Input = () => {
        if (props.isReadOnly) {
            return <ReadonlyComponent type={props.type} value={props.value} options={props.options} />;
        }

        switch (props.type) {
            case 'number':
                return (
                    <NumberInputComponent
                        value={props.value}
                        leaveBlank={leaveBlank}
                        dataObject={props.dataObject}
                        extraArgs={props.extraArgs}
                        allowNegativeValues={props.allowNegativeValues}
                        min={props.min}
                        max={props.max}
                        name={props.name}
                        tabIndex={props.tabIndex}
                        elementId={props.elementId}
                        onChange={handleChange}
                        maxDecimals={props.maxDecimals ?? DEFAULT_DECIMALS}
                        inputSize={props.inputSize}
                    />
                );
            case 'text':
                return <TextInputComponent value={props.value} name={props.name} elementId={props.elementId} onChange={handleChange} />;
            case 'textarea':
                return (
                    <TextAreaInputComponent
                        value={props.value}
                        name={props.name}
                        elementId={props.elementId}
                        onChange={handleChange}
                        maxTextLength={props.maxTextLength}
                        tabIndex={props.tabIndex}
                    />
                );
            case 'select':
                return (
                    <SelectInputComponent
                        value={props.value}
                        name={props.name}
                        options={props.options}
                        selectDefaultOption={props.selectDefaultOption}
                        elementId={props.elementId}
                        onChange={handleChange}
                        tabIndex={props.tabIndex}
                        labelMinWidth={props.labelMinWidth}
                        textAlign={props.textAlign}
                    />
                );
            case 'checkbox':
                return (
                    <CheckboxInputComponent
                        value={props.value}
                        name={props.name}
                        elementId={props.elementId}
                        onChange={handleChange}
                        tabIndex={props.tabIndex}
                    />
                );
            case 'time':
                return <TimeInputComponent value={props.value} onTimeInputChange={onTimeInputChange} />;
            case 'date':
                return <DateInputComponent value={props.value} elementId={props.elementId} onDateInputChange={onDateInputChange} />;
        }

        return <></>;
    };

    const getInputClassName = (): string => {
        return (
            'input-value' +
            (props.isReadOnly ? ' read-only-input' : '') +
            (props.unit === '℃' ? ' temperature-input' : '') +
            (props.type === 'checkbox' ? ' checkbox-input' : '') +
            (props.type === 'textarea' ? ' textarea-input' : '')
        );
    };

    if (props.hideIfEmpty && (props.value === null || props.value === undefined || props.value === 0)) {
        return <></>;
    }

    return (
        <>
            <InputElementWrapper isValid={isValid} hasWarning={!!props.warningText} labelMinWidth={props.labelMinWidth} textAlign={props.textAlign}>
                <label title={props.title ? props.title : ''}>
                    <TooltipComponent
                        hideIfEmpty={props.hideIfEmpty}
                        value={props.value}
                        tooltipId={props.tooltipId}
                        description={props.description}
                    />
                    <div className={'input-value-wrapper'}>
                        <CostComponent cost={props.cost} />
                        <span className={getInputClassName()}>
                            {props.validationInTooltip ? (
                                <Tooltip
                                    type={isValid ? 'warning' : 'error'}
                                    disabled={isValid && !props.warningText}
                                    text={
                                        isValid ? (
                                            props.warningText
                                        ) : (
                                            <ValidationComponent
                                                isValid={isValid}
                                                type={props.type}
                                                selectedCustomValidation={selectedCustomValidation}
                                                min={props.min}
                                                max={props.max}
                                                unit={props.unit}
                                            />
                                        )
                                    }
                                >
                                    {Input()}
                                </Tooltip>
                            ) : (
                                Input()
                            )}
                        </span>
                        {!props.disableUnit && <span className={'input-unit'}>{props.unit}</span>}
                    </div>
                </label>
                {!props.validationInTooltip && (
                    <ValidationComponent
                        isValid={isValid}
                        type={props.type}
                        selectedCustomValidation={selectedCustomValidation}
                        min={props.min}
                        max={props.max}
                        unit={props.unit}
                    />
                )}
                {!props.validationInTooltip && props.warningText && <WarningText>{props.warningText}</WarningText>}
                {/* {props.validationInTooltip && !isValid && <ErrorInfoIcon tooltipText={props.warningText} isReadOnly={props.isReadOnly} />} */}
            </InputElementWrapper>
        </>
    );
};

export default OdfjellInputComponent;
